import React from "react";
import { createPortal } from "./windowManager/browserFenster/fensterPortal";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "./risikoanalyseStep/tokens";
import { useAnredeOptionen } from "./forms/useAnredeOptionen";
import {
  fetchLegalFormOptions,
  type LegalFormOption,
} from "../utils/legalFormOptions";
import {
  DokumentScanFehler,
  hatVorschau,
  leseFuehrerschein,
  leseGeburtsurkunde,
  leseHeiratsurkunde,
  leseGehaltsabrechnung,
  lesePersonalausweis,
  leseSterbeurkunde,
  leseVcardDatei,
  leseVisitenkarte,
} from "../services/dokumentScanService";
import {
  BEFUND_LABELS,
  BEFUND_REIHENFOLGE,
  seitenZusammenfuehren,
  visitenkarteZuFirmaFormState,
  visitenkarteKanaeleOrdnen,
  visitenkarteZuFirmaRef,
  visitenkarteZuMenschEntry,
  visitenkarteZuPrivatFormState,
  vcardZuPrivatFormState,
  type MapperErgebnis,
  type MapperKontext,
  type VisitenkarteBefund,
} from "../utils/visitenkarteZuFormState";
import {
  AUSWEIS_LABELS,
  AUSWEIS_REIHENFOLGE,
  ausweisSeitenZusammenfuehren,
  ausweisZuPrivatFormState,
  type PersonalausweisBefund,
} from "../utils/personalausweisZuFormState";
import {
  GEHALT_LABELS,
  GEHALT_REIHENFOLGE,
  gehaltAnzeigewert,
  gehaltZuPrivatFormState,
  type GehaltsabrechnungBefund,
} from "../utils/gehaltsabrechnungZuFormState";
import { istVcard, vcardLesen } from "../utils/vcardLesen";
import {
  FUEHRERSCHEIN_LABELS,
  FUEHRERSCHEIN_REIHENFOLGE,
  GEBURTSURKUNDE_LABELS,
  GEBURTSURKUNDE_REIHENFOLGE,
  HEIRATSURKUNDE_LABELS,
  HEIRATSURKUNDE_REIHENFOLGE,
  FAMILIENSTAND_VERHEIRATET,
  eigenerEhegatte,
  heiratEhepartner,
  heiratsurkundeZuPrivatFormState,
  type HeiratsurkundeBefund,
  STERBEURKUNDE_LABELS,
  STERBEURKUNDE_REIHENFOLGE,
  fuehrerscheinZuPrivatFormState,
  geburtsurkundeZuPrivatFormState,
  sterbeurkundeZuPrivatFormState,
  urkundeAnzeigewert,
  urkundeSeitenZusammenfuehren,
  type FuehrerscheinBefund,
  type GeburtsurkundeBefund,
  type SterbeurkundeBefund,
} from "../utils/urkundenZuFormState";
import {
  DokumentAuswahl,
  type DokumentAuswahlErgebnis,
} from "./documents/auswahl/DokumentAuswahl";
import { dokumentAlsDatei } from "./documents/auswahl/dokumentAlsDatei";
import {
  alsBestandsFirmen,
  firmenDomain,
  firmenSuchbegriff,
  firmenTrefferBewerten,
  type FirmenTreffer,
} from "../utils/visitenkarteFirmensuche";
import { apiUrl } from "../utils/api";
import {
  korrekturenUeberlagern,
  korrekturInputMode,
  korrekturPruefen,
  korrekturTyp,
  korrekturWert,
} from "../utils/scanKorrektur";
import { useInlineWertEingabeStyles } from "./inlineWertEingabe";
import type { ContactRef } from "./relationsInline/types";
import type { VerantwortlichkeitValue } from "./verantwortlichkeit/VerantwortlichkeitInput";

/**
 * „Datei scannen" — Visitenkarte, Personalausweis, Gehaltsabrechnung oder
 * vCard aufnehmen bzw. auswählen, prüfen, übernehmen.
 *
 * Optik und Ablauf folgen `FirmendatenBefuellenDialog`: Befund holen →
 * Feldauswahl → übernehmen, dabei nur leere Felder füllen und jeden Wert
 * vorher zeigen. Die vier Dokumentarten teilen sich diese ganze Strecke; sie
 * unterscheiden sich allein in Endpunkt, Feldliste und Zuordnung, und genau
 * das steht in `ARTEN` bzw. `leseSeite`/`mappeBefund`.
 *
 * Warum je Seite ein eigener Request: Visitenkarte und Ausweis sind
 * zweiseitig — beim Ausweis steht die Anschrift hinten —, und das nginx-Limit
 * von 12 MB gilt für den GESAMTEN Request. Zusammengeführt wird hier.
 *
 * Die vCard ist der einzige Weg ohne Server: eine .vcf ist bereits
 * strukturiert und wird im Browser gelesen. Sie verlässt den Rechner nicht.
 *
 * HEIC (iPhone-Standard) wird unverändert hochgeladen: die Texterkennung liest
 * es, der Browser kann es nur nicht anzeigen — deshalb entfällt für solche
 * Dateien die Vorschau, nicht die Aufnahme.
 *
 * KEIN Dokument wird gespeichert. Beim Ausweis ist das nicht Sparsamkeit,
 * sondern Voraussetzung: Ausweisdaten dürfen nur mit Einwilligung erhoben
 * werden (§ 20 PAuswG), und die Vorratshaltung des Scans wäre unzulässig.
 * Übrig bleibt allein, was der Anwender hier ausdrücklich übernimmt.
 *
 * Daran ändert der zweite Weg „Aus KMVS wählen" (PO 22.09.2026) nichts: Der
 * Beleg kann schon in KMVS liegen — etwa der Ausweis-Scan, den ein Kollege
 * zur Legitimation abgelegt hat —, und bis dahin führte der einzige Weg
 * dorthin über das Herunterladen, um ihn gleich wieder auszuwählen.
 * Ausgewählte Dokumente werden GELESEN wie jede andere Datei und bleiben,
 * wo sie sind; dieser Dialog legt weiterhin nichts an und löscht nichts.
 */

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 100002,
    backgroundColor: "rgba(20,8,20,0.34)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: REFINED.fontUI,
  },
  surface: {
    width: "min(640px, 100%)",
    maxHeight: "min(86vh, 860px)",
    display: "flex",
    flexDirection: "column",
    backgroundColor: REFINED.white,
    border: `1px solid ${REFINED.line}`,
    borderRadius: "14px",
    boxShadow: REFINED.cardShadow,
    overflow: "hidden",
    color: REFINED.ink,
  },
  kopf: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    padding: "15px 18px",
    borderBottom: `2px solid ${REFINED.goldLine}`,
  },
  titel: { margin: 0, fontSize: `${REFINED.fs.lg}px`, fontWeight: 800 },
  koerper: {
    padding: "16px 18px",
    overflowY: "auto",
    flexGrow: 1,
    fontSize: `${REFINED.fs.sm}px`,
    color: REFINED.ink2,
    lineHeight: 1.5,
  },
  fuss: {
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "10px",
    padding: "12px 18px",
    borderTop: `1px solid ${REFINED.line}`,
    backgroundColor: REFINED.fieldBg,
    flexWrap: "wrap",
  },
  btn: {
    appearance: "none",
    font: "inherit",
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    padding: "8px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    backgroundColor: REFINED.white,
    border: `1px solid ${REFINED.aub100}`,
    color: REFINED.aub,
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  btnPrimaer: {
    backgroundColor: REFINED.aub,
    border: `1px solid ${REFINED.aub}`,
    color: REFINED.white,
  },
  seiten: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "12px",
    marginBottom: "14px",
  },
  kachel: {
    appearance: "none",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    padding: "14px",
    borderRadius: "12px",
    ...shorthands.border("1px", "dashed", REFINED.aub100),
    backgroundColor: REFINED.fieldBg,
    color: REFINED.ink,
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    minHeight: "104px",
    ":hover": { ...shorthands.borderColor(REFINED.aub) },
    ":disabled": { opacity: 0.6, cursor: "not-allowed" },
  },
  kachelGefuellt: {
    ...shorthands.border("1px", "solid", REFINED.aub),
    backgroundColor: REFINED.white,
  },
  /**
   * Der zweite Weg unter den Kacheln: „Aus KMVS wählen". Eine Textzeile, kein
   * zweiter Kasten — die Kachel bleibt der Hauptweg, weil der Beleg in aller
   * Regel frisch eingescannt auf dem Rechner liegt.
   */
  kmvsZeile: {
    display: "flex",
    justifyContent: "center",
    gap: "14px",
    marginTop: "-6px",
    marginBottom: "14px",
    flexWrap: "wrap",
  },
  kmvsKnopf: {
    appearance: "none",
    font: "inherit",
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 700,
    color: REFINED.aub,
    backgroundColor: "transparent",
    ...shorthands.border("0"),
    cursor: "pointer",
    textDecorationLine: "underline",
    textUnderlineOffset: "2px",
    ":disabled": { opacity: 0.5, cursor: "default" },
  },
  /**
   * Die Auswahl liegt ÜBER dem Scan-Dialog — sie wird aus ihm heraus geöffnet
   * und muss ihn verdecken, nicht neben ihm stehen.
   */
  auswahlBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 100003,
    backgroundColor: "rgba(20,8,20,0.34)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: REFINED.fontUI,
  },
  auswahlFlaeche: {
    width: "min(920px, calc(100vw - 32px))",
    height: "min(540px, calc(100vh - 32px))",
    position: "relative",
    display: "flex",
    backgroundColor: "#fff",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 24px 70px -24px rgba(58,12,49,0.42)",
  },
  kachelTitel: { fontWeight: 800, fontSize: `${REFINED.fs.sm}px` },
  kachelHinweis: { fontSize: `${REFINED.fs.xs}px`, color: REFINED.ink2 },
  vorschau: {
    marginTop: "6px",
    maxHeight: "84px",
    maxWidth: "100%",
    objectFit: "contain",
    borderRadius: "6px",
  },
  zeile: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
    padding: "7px 0",
    borderBottom: `1px solid ${REFINED.line}`,
  },
  // Feste Spaltenbreite, damit die Eingabefelder untereinander fluchten;
  // paddingTop/marginTop: Feldname und Haken stehen auf der Textlinie des
  // Eingabefelds daneben (T-84, Inline-Bearbeitung wie beim Impressum).
  zeileLabel: { flex: "0 0 150px", fontWeight: 700, color: REFINED.ink, paddingTop: "8px" },
  zeileHaken: { marginTop: "11px", accentColor: REFINED.aub, flexShrink: 0 },
  zeileText: { display: "block", paddingTop: "8px" },
  zeileWert: { flexGrow: 1, wordBreak: "break-word" },
  belegt: { fontSize: `${REFINED.fs.xs}px`, color: REFINED.ink2, fontStyle: "italic" },
  hinweisKasten: {
    marginTop: "14px",
    padding: "10px 12px",
    borderRadius: "10px",
    backgroundColor: REFINED.fieldBg,
    border: `1px solid ${REFINED.line}`,
    fontSize: `${REFINED.fs.xs}px`,
  },
  fehler: {
    marginTop: "10px",
    padding: "10px 12px",
    borderRadius: "10px",
    backgroundColor: "#fdf2f2",
    border: "1px solid #e3b7b7",
    color: "#8a1f1f",
    fontSize: `${REFINED.fs.xs}px`,
  },
  liste: { margin: "6px 0 0", paddingLeft: "18px" },
  versteckt: { display: "none" },

  // ── Artwahl ────────────────────────────────────────────────────────────
  artGitter: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "10px",
  },
  artKachel: {
    appearance: "none",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    padding: "13px 14px",
    borderRadius: "12px",
    ...shorthands.border("1px", "solid", REFINED.line),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    display: "flex",
    alignItems: "flex-start",
    columnGap: "11px",
    ":hover": { ...shorthands.borderColor(REFINED.aub), backgroundColor: REFINED.fieldBg },
  },
  // „Kontakt vom Handy" (T-356): über die volle Breite und betont — der
  // häufigste Weg auf dem Handy, er darf nicht unter den Belegen verschwinden.
  artKachelHervor: {
    gridColumn: "1 / -1",
    ...shorthands.borderColor(REFINED.aub),
    backgroundColor: REFINED.fieldBg,
  },
  // Arten ohne Texterkennung: sichtbar, damit klar ist, dass sie kommen —
  // aber nicht wählbar.
  artKachelAus: {
    cursor: "not-allowed",
    opacity: 0.45,
    ":hover": { ...shorthands.borderColor(REFINED.line), backgroundColor: REFINED.white },
  },
  artIcon: { color: REFINED.aub, flexShrink: 0, marginTop: "1px" },
  artTitel: { fontWeight: 800, fontSize: `${REFINED.fs.sm}px`, display: "block" },
  artHinweis: {
    fontSize: `${REFINED.fs.xs}px`,
    color: REFINED.ink2,
    display: "block",
    marginTop: "2px",
    lineHeight: 1.45,
  },
  zurueck: {
    appearance: "none",
    font: "inherit",
    fontSize: `${REFINED.fs.xs}px`,
    fontWeight: 700,
    background: "none",
    ...shorthands.border("0"),
    color: REFINED.aub,
    cursor: "pointer",
    padding: "0 0 10px",
  },
  geprueft: {
    fontSize: `${REFINED.fs.xs}px`,
    color: "#1f6b3a",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  korrekturFehler: { fontSize: `${REFINED.fs.xs}px`, color: "#b42318", marginTop: "3px" },
});

type Ziel = "privat" | "firma";

export type ScanArtId =
  | "visitenkarte"
  | "personalausweis"
  | "gehaltsabrechnung"
  | "vcard"
  | "fuehrerschein"
  | "geburtsurkunde"
  | "heiratsurkunde"
  | "sterbeurkunde";

/**
 * Die Personendokumente, deren Scan zusätzlich als Datei in der Akte abgelegt
 * werden kann. Der Personalausweis gehört ausdrücklich NICHT dazu: Sein Scan
 * wird nie gespeichert (§ 20 PAuswG, siehe Dateikopf) — wer die Kopie ablegen
 * will, lädt sie am Dokumenteintrag bewusst hoch.
 */
const ABLEGBAR: ReadonlySet<ScanArtId> = new Set([
  "fuehrerschein",
  "geburtsurkunde",
  "heiratsurkunde",
  "sterbeurkunde",
]);

/** Die Personenstandsurkunden — sie teilen Zusammenführung und Anzeige. */
const URKUNDEN: ReadonlySet<ScanArtId> = ABLEGBAR;

/** Nur diese Arten nennen eine Firma, die verknüpft werden kann. */
const MIT_GEGENSEITE: ReadonlySet<ScanArtId> = new Set(["visitenkarte", "vcard", "gehaltsabrechnung"]);

/**
 * Was der Dialog zurückgibt.
 *
 * Visitenkarte, vCard und Gehaltsabrechnung nennen fast immer Person UND
 * Firma. Die Maske füllt nur eine der beiden — die andere Seite kommt deshalb
 * als Verknüpfung zurück: in der Personen-Maske die Firma als Eintrag unter
 * „Verbindung zu Unternehmen", in der Firmen-Maske die Person als Mensch im
 * Unternehmen. Angelegt wird in beiden Fällen erst beim Speichern (P9).
 */
export type DateiScanUebernahme = {
  werte: Record<string, string>;
  /**
   * Personen-Maske: die Firma für „Verbindung zu Unternehmen" — bestehend
   * (`contactId`) oder neu (`manual`, mit den Firmendaten der Karte
   * vorbefüllt). Die Position der Karte wird die Rolle.
   */
  firmaVerbindung?: { ref: ContactRef; position?: string };
  /** Firmen-Maske: Person, die zu den Verantwortlichkeiten kommt. */
  mensch?: { id: string; ref: ContactRef; verant: VerantwortlichkeitValue };
  /** Welche Art gelesen wurde — bestimmt den Dokumenteintrag. */
  scanArt?: ScanArtId;
  /**
   * Personen-Maske, Visitenkarte/vCard: die Firma der Karte — verknüpft oder
   * nur dem Namen nach. Ihr werden die geschäftlichen Kanäle zugeordnet.
   */
  firma?: { id?: number; name: string };
  /** Die gelesene Datei, wenn der Anwender sie in der Akte ablegen will. */
  ablegen?: File;
  /**
   * Heiratsurkunde: der andere Ehegatte als Vorbelegung für den Haushalt
   * (U3) — angelegt wird er erst beim Speichern.
   */
  ehepartner?: { ref: ContactRef; relationLabel: string };
};

type Props = {
  open: boolean;
  /** Bestimmt, in welche Maske die Werte übernommen werden. */
  ziel: Ziel;
  /** Aktuelle Formularwerte — belegte Zielfelder werden nicht vorausgewählt. */
  vorhanden: Record<string, string>;
  /**
   * Ob die Gegenseite verknüpft werden kann. Beim Produktpartner ist die
   * Verknüpfungs-Sektion der Firmen-Maske ausgeblendet (seine Personen sind
   * Ansprechpartner und werden an der Person gepflegt) — dort würde ein
   * Eintrag unsichtbar versickern, deshalb bietet der Dialog ihn erst gar
   * nicht an.
   */
  gegenseiteMoeglich?: boolean;
  /** Direkt mit dieser Art starten (z. B. Knopf „Gehaltsabrechnung"). */
  startArt?: ScanArtId;
  /** Nur diese Arten anbieten; ohne Angabe alle, die zur Maske passen. */
  erlaubteArten?: ScanArtId[];
  /** Ausgegraut angezeigt — die Texterkennung dafür gibt es noch nicht. */
  folgendeArten?: string[];
  /** Überschrift der Artwahl, Standard „Datei scannen". */
  titel?: string;
  /**
   * Bestehender Kontakt (Bearbeiten/Umwandeln): seine Akte ist in „Aus KMVS
   * wählen" vorbelegt (T-41). Fehlt er (Neuanlage), zeigt die Fläche ihren
   * Kundenpicker.
   */
  kontakt?: { id: number; name?: string | null } | null;
  onClose: () => void;
  onUebernehmen: (ergebnis: DateiScanUebernahme) => void;
};

/** Ein beliebiger Dokumentbefund — die Feldnamen hängen an der Dokumentart. */
type Befund = Record<string, unknown>;

type Seite = { datei: File; befund: Befund | null };

const IconKarte: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <rect x="1.8" y="3.4" width="12.4" height="9.2" rx="1.5" />
    <circle cx="5.3" cy="7.1" r="1.4" />
    <path d="M3.3 11.1c.3-1 1.1-1.6 2-1.6s1.7.6 2 1.6" />
    <path d="M9.4 6.6h3.1M9.4 9h3.1" />
  </svg>
);

const IconAusweis: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <rect x="1.4" y="3" width="13.2" height="10" rx="1.4" />
    <circle cx="5.2" cy="6.9" r="1.5" />
    <path d="M2.9 10.8c.3-1.1 1.2-1.7 2.3-1.7s2 .6 2.3 1.7" />
    <path d="M9.6 6.2h3.4M9.6 8.3h3.4" />
    <path d="M2.6 11.9h10.8" strokeDasharray="1.2 1" />
  </svg>
);

const IconAbrechnung: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <path d="M3.4 1.7h6l3.2 3.2v9.4H3.4z" />
    <path d="M9.2 1.9v3.2h3.2" />
    <path d="M5.4 8h5.2M5.4 10h5.2M5.4 12h3.1" />
  </svg>
);

const IconVcard: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <path d="M8 1.9v7.4" strokeLinecap="round" />
    <path d="M5.4 6.8 8 9.4l2.6-2.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.6 10.6v2.1c0 .8.6 1.4 1.4 1.4h8c.8 0 1.4-.6 1.4-1.4v-2.1" strokeLinecap="round" />
  </svg>
);

/**
 * Was der Dateidialog anbietet. PDF gehört ausdrücklich dazu — nicht nur bei
 * der Abrechnung: Ausweiskopien und eingescannte Karten liegen genauso oft als
 * PDF im Downloads-Ordner, und `accept="image/*"` blendet sie im Dateidialog
 * aus, ohne zu sagen warum. Die Texterkennung liest PDF ohnehin.
 */
const BELEG_FORMATE = "application/pdf,image/*";

type ArtKonfig = {
  id: ScanArtId;
  titel: string;
  /** Was der Anwender in der Artwahl liest. */
  hinweis: string;
  icon: React.FC;
  /** Personendokumente haben in der Firmen-Maske nichts zu suchen. */
  nurPrivat: boolean;
  accept: string;
  seiten: Array<{ welche: "vorne" | "hinten"; titel: string; hinweis: string }>;
  labels: Record<string, string>;
  reihenfolge: string[];
};

const ARTEN: ArtKonfig[] = [
  // Ganz oben und über die volle Breite (T-356): Auf dem Handy lag die Kachel
  // als letzte von acht unter dem sichtbaren Bereich, und „vCard" sagte dem
  // Anwender nicht, dass hier ein Kontakt vom Handy übernommen wird.
  {
    id: "vcard",
    titel: "Kontakt vom Handy übernehmen (vCard)",
    hinweis:
      "Kontaktkarte aus dem iPhone- oder Android-Adressbuch oder aus Outlook (.vcf). Wird nur im Browser gelesen — ohne Texterkennung.",
    icon: IconVcard,
    nurPrivat: false,
    // `text/directory`: ältere Exporte melden diesen Typ — ohne ihn kann die
    // Dateiauswahl die Datei ausgrauen.
    accept: ".vcf,text/vcard,text/x-vcard,text/directory",
    seiten: [{ welche: "vorne", titel: "Kontaktkarte (.vcf)", hinweis: ".vcf-Datei auswählen" }],
    labels: BEFUND_LABELS,
    reihenfolge: BEFUND_REIHENFOLGE as string[],
  },
  {
    id: "visitenkarte",
    titel: "Visitenkarte",
    hinweis: "Foto oder Scan der Karte — Vorder- und Rückseite. Füllt Person und Firma.",
    icon: IconKarte,
    nurPrivat: false,
    accept: BELEG_FORMATE,
    seiten: [
      { welche: "vorne", titel: "Vorderseite", hinweis: "Foto, Scan oder PDF" },
      {
        welche: "hinten",
        titel: "Rückseite (optional)",
        hinweis: "nur, wenn dort Angaben stehen",
      },
    ],
    labels: BEFUND_LABELS,
    reihenfolge: BEFUND_REIHENFOLGE as string[],
  },
  {
    id: "personalausweis",
    titel: "Personalausweis",
    hinweis:
      "Foto beider Seiten. Die Anschrift steht hinten; die maschinenlesbare Zone prüft Nummer und Datum über ihre Prüfziffern.",
    icon: IconAusweis,
    nurPrivat: true,
    accept: BELEG_FORMATE,
    seiten: [
      { welche: "vorne", titel: "Vorderseite", hinweis: "Seite mit Lichtbild und Namen" },
      { welche: "hinten", titel: "Rückseite", hinweis: "Anschrift und maschinenlesbare Zone" },
    ],
    labels: AUSWEIS_LABELS,
    reihenfolge: AUSWEIS_REIHENFOLGE,
  },
  {
    id: "gehaltsabrechnung",
    titel: "Gehaltsabrechnung",
    hinweis:
      "PDF oder Foto. Liefert Steuerklasse, Anschrift und Arbeitgeber — das Jahreseinkommen nur, wenn ein Jahreswert ausgewiesen ist.",
    icon: IconAbrechnung,
    nurPrivat: true,
    accept: BELEG_FORMATE,
    seiten: [{ welche: "vorne", titel: "Abrechnung", hinweis: "PDF, Scan oder Foto" }],
    labels: GEHALT_LABELS,
    reihenfolge: GEHALT_REIHENFOLGE,
  },
  {
    id: "fuehrerschein",
    titel: "Führerschein",
    hinweis: "Foto beider Seiten des Kartenführerscheins. Die Klassen stehen hinten.",
    icon: IconAusweis,
    nurPrivat: true,
    accept: BELEG_FORMATE,
    seiten: [
      { welche: "vorne", titel: "Vorderseite", hinweis: "Seite mit Lichtbild und Nummer" },
      { welche: "hinten", titel: "Rückseite (optional)", hinweis: "Klassentabelle" },
    ],
    labels: FUEHRERSCHEIN_LABELS,
    reihenfolge: FUEHRERSCHEIN_REIHENFOLGE,
  },
  {
    id: "geburtsurkunde",
    titel: "Geburtsurkunde",
    hinweis: "PDF, Scan oder Foto. Liefert Geburtsdatum, -zeit und -ort, Registernummer und Standesamt.",
    icon: IconAbrechnung,
    nurPrivat: true,
    accept: BELEG_FORMATE,
    seiten: [{ welche: "vorne", titel: "Urkunde", hinweis: "PDF, Scan oder Foto" }],
    labels: GEBURTSURKUNDE_LABELS,
    reihenfolge: GEBURTSURKUNDE_REIHENFOLGE,
  },
  {
    id: "heiratsurkunde",
    titel: "Heiratsurkunde",
    hinweis:
      "PDF, Scan oder Foto. Liefert Hochzeitstag, Name und Geburtsname und schlägt den Ehepartner für den Haushalt vor.",
    icon: IconAbrechnung,
    nurPrivat: true,
    accept: BELEG_FORMATE,
    seiten: [{ welche: "vorne", titel: "Urkunde", hinweis: "PDF, Scan oder Foto" }],
    labels: HEIRATSURKUNDE_LABELS,
    reihenfolge: HEIRATSURKUNDE_REIHENFOLGE,
  },
  {
    id: "sterbeurkunde",
    titel: "Sterbeurkunde",
    hinweis: "PDF, Scan oder Foto. Schlägt das Sterbedatum vor — nur, wenn es auf der Urkunde steht.",
    icon: IconAbrechnung,
    nurPrivat: true,
    accept: BELEG_FORMATE,
    seiten: [{ welche: "vorne", titel: "Urkunde", hinweis: "PDF, Scan oder Foto" }],
    labels: STERBEURKUNDE_LABELS,
    reihenfolge: STERBEURKUNDE_REIHENFOLGE,
  },
];

const DateiScannenDialog: React.FC<Props> = ({
  open,
  ziel,
  vorhanden,
  gegenseiteMoeglich = true,
  startArt,
  erlaubteArten,
  folgendeArten = [],
  titel = "Datei scannen",
  kontakt = null,
  onClose,
  onUebernehmen,
}) => {
  const styles = useStyles();
  const inline = useInlineWertEingabeStyles();
  const anredeOptionen = useAnredeOptionen();
  const [rechtsformen, setRechtsformen] = React.useState<LegalFormOption[]>([]);

  const [artId, setArtId] = React.useState<ScanArtId | null>(null);
  const [vorderseite, setVorderseite] = React.useState<Seite | null>(null);
  const [rueckseite, setRueckseite] = React.useState<Seite | null>(null);
  const [laeuft, setLaeuft] = React.useState<"vorne" | "hinten" | null>(null);
  /** Für welche Seite ist die KMVS-Auswahl gerade offen? */
  const [kmvsFuer, setKmvsFuer] = React.useState<"vorne" | "hinten" | null>(null);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [warnung, setWarnung] = React.useState<string | null>(null);
  const [abgewaehlt, setAbgewaehlt] = React.useState<Set<string>>(new Set());
  /** Urkunden: die gelesene Datei zusätzlich in der Akte ablegen. */
  const [dateiAblegen, setDateiAblegen] = React.useState(true);
  /** Heiratsurkunde: welcher Ehegatte der Kontakt ist (`null` = erkannt). */
  const [ehegatteGewaehlt, setEhegatteGewaehlt] = React.useState<1 | 2 | null>(null);
  /** Heiratsurkunde: den anderen Ehegatten im Haushalt vorschlagen. */
  const [ehepartnerAn, setEhepartnerAn] = React.useState(true);
  /**
   * T-84: Korrekturen des Anwenders über dem erkannten Befund. Ein Schlüssel
   * mit `undefined` heißt „geleert". Sie fließen VOR allen Ableitungen in
   * `felder` ein — Firmensuche, Gegenseite und Übernahme sehen den
   * korrigierten Wert.
   */
  const [korrekturen, setKorrekturen] = React.useState<Record<string, unknown>>({});
  /**
   * Eingetippter, noch nicht übernommener Text je Zeile. Er wird beim
   * Verlassen des Felds (oder Enter) zur Korrektur — nicht bei jedem
   * Tastendruck, sonst liefe z. B. die Firmensuche mit jedem Buchstaben.
   */
  const [entwuerfe, setEntwuerfe] = React.useState<Record<string, string>>({});
  /** Meldung der Mindestprüfung je Zeile (ungültige E-Mail, Rufnummer …). */
  const [entwurfFehler, setEntwurfFehler] = React.useState<Record<string, string>>({});
  /** Hinweis, wenn ein neuer Scan Korrekturen ersetzt hat. */
  const [korrekturHinweis, setKorrekturHinweis] = React.useState<string | null>(null);

  // Verknüpfung zur jeweils anderen Seite des Dokuments.
  const [gegenseiteAn, setGegenseiteAn] = React.useState(true);
  /** Bestandsfirmen, die zur Karte passen (Name, auch unscharf, oder Domain). */
  const [trefferListe, setTrefferListe] = React.useState<FirmenTreffer[]>([]);
  /** Gewählte Bestandsfirma — `null` = als neue Firma anlegen. */
  const [gewaehlteFirmaId, setGewaehlteFirmaId] = React.useState<number | null>(null);
  const [sucheLaeuft, setSucheLaeuft] = React.useState(false);

  const vorneRef = React.useRef<HTMLInputElement>(null);
  const hintenRef = React.useRef<HTMLInputElement>(null);

  const art = React.useMemo(() => ARTEN.find((a) => a.id === artId) ?? null, [artId]);
  const verfuegbar = React.useMemo(
    () =>
      ARTEN.filter(
        (a) => (ziel === "privat" || !a.nurPrivat) && (!erlaubteArten || erlaubteArten.includes(a.id)),
      ),
    [ziel, erlaubteArten],
  );
  // Mit Startart geht es ohne Artwahl direkt zum Hochladen.
  React.useEffect(() => {
    if (open && startArt) setArtId(startArt);
  }, [open, startArt]);
  // Gibt es nichts anderes zu wählen, entfällt „← andere Art wählen".
  const artWechselbar = verfuegbar.length + folgendeArten.length > 1;

  // Der Rechtsform-Katalog wird nur gebraucht, wenn eine Firma angelegt wird.
  React.useEffect(() => {
    if (!open || rechtsformen.length) return;
    let abgebrochen = false;
    fetchLegalFormOptions()
      .then((optionen) => {
        if (!abgebrochen) setRechtsformen(optionen);
      })
      .catch(() => {
        // Ohne Katalog bleibt die Rechtsform leer und wird als übergangen
        // gemeldet — die Übernahme der übrigen Felder soll das nicht aufhalten.
      });
    return () => {
      abgebrochen = true;
    };
  }, [open, rechtsformen.length]);

  const befundZuruecksetzen = React.useCallback(() => {
    setVorderseite(null);
    setRueckseite(null);
    setLaeuft(null);
    setFehler(null);
    setWarnung(null);
    setAbgewaehlt(new Set());
    setDateiAblegen(true);
    setEhegatteGewaehlt(null);
    setEhepartnerAn(true);
    setGegenseiteAn(true);
    setTrefferListe([]);
    setGewaehlteFirmaId(null);
    setSucheLaeuft(false);
    setKorrekturen({});
    setEntwuerfe({});
    setEntwurfFehler({});
    setKorrekturHinweis(null);
  }, []);

  const zuruecksetzen = React.useCallback(() => {
    befundZuruecksetzen();
    setArtId(null);
  }, [befundZuruecksetzen]);

  const schliessen = () => {
    zuruecksetzen();
    onClose();
  };

  const kontext: MapperKontext = React.useMemo(
    () => ({
      anredeOptionen: anredeOptionen.map((o) => o.salutation),
      titelOptionen: ["Dr.", "Prof.", "Prof. Dr."],
      rechtsformOptionen: rechtsformen,
    }),
    [anredeOptionen, rechtsformen],
  );

  /** Beide Aufnahmen zusammenführen — beim Ausweis gewinnt die geprüfte MRZ. */
  const { felder: befundFelder, konflikte } = React.useMemo(() => {
    const vorne = vorderseite?.befund ?? {};
    const hinten = rueckseite?.befund ?? {};
    if (artId === "personalausweis") {
      return ausweisSeitenZusammenfuehren(
        vorne as PersonalausweisBefund,
        hinten as PersonalausweisBefund,
      ) as { felder: Befund; konflikte: Array<{ feld: string; vorne: string; hinten: string }> };
    }
    if (artId === "gehaltsabrechnung") {
      return { felder: vorne as Befund, konflikte: [] };
    }
    if (artId === "vcard") {
      // Die vCard wird im Browser gelesen: Zentrale und Person trennen hier
      // dieselben Regeln wie beim Server-Befund (V3–V6).
      return { felder: visitenkarteKanaeleOrdnen(vorne as VisitenkarteBefund) as Befund, konflikte: [] };
    }
    if (artId && URKUNDEN.has(artId)) {
      return urkundeSeitenZusammenfuehren(vorne, hinten);
    }
    const zusammen = seitenZusammenfuehren(vorne as VisitenkarteBefund, hinten as VisitenkarteBefund);
    // Nach dem Zusammenführen neu ordnen: Die Zentrale kann auf der Rückseite
    // stehen, die Durchwahl vorne — eine je Seite abgeleitete Zentrale wäre
    // dann überflüssig.
    return { felder: visitenkarteKanaeleOrdnen(zusammen.felder) as Befund, konflikte: zusammen.konflikte };
  }, [artId, vorderseite, rueckseite]);

  /** T-84: der Befund mit den Korrekturen des Anwenders. */
  const felder = React.useMemo(
    () => korrekturenUeberlagern(befundFelder, korrekturen),
    [befundFelder, korrekturen],
  );

  /** Befund → Formularwerte. Die einzige Stelle, an der die Art zählt. */
  /**
   * Heiratsurkunde: welcher Ehegatte der Kontakt ist — erkannt aus dem, was in
   * der Maske schon steht, oder vom Anwender im Dialog gewählt.
   */
  const heirat = React.useMemo(() => {
    if (artId !== "heiratsurkunde") return null;
    // Aus `felder`, nicht aus dem Rohbefund: Korrekturen (T-84) gelten auch für
    // den Ehepartner-Vorschlag.
    const b = felder as HeiratsurkundeBefund;
    const erkannt = eigenerEhegatte(b, vorhanden);
    return {
      befund: b,
      selbst: ehegatteGewaehlt ?? erkannt.nr,
      sicher: ehegatteGewaehlt != null || erkannt.sicher,
      ehename: b.ehename,
    };
  }, [artId, felder, vorhanden, ehegatteGewaehlt]);

  const vcardMitFirma = typeof felder.firma === "string" && felder.firma.trim() !== "";

  const mappe = React.useCallback(
    (befund: Befund): MapperErgebnis => {
      switch (artId) {
        case "personalausweis":
          return ausweisZuPrivatFormState(befund as PersonalausweisBefund, {
            anredeOptionen: kontext.anredeOptionen,
          });
        case "gehaltsabrechnung":
          return gehaltZuPrivatFormState(befund as GehaltsabrechnungBefund, {
            titelOptionen: kontext.titelOptionen,
          });
        case "fuehrerschein":
          return fuehrerscheinZuPrivatFormState(befund as FuehrerscheinBefund);
        case "geburtsurkunde":
          return geburtsurkundeZuPrivatFormState(befund as GeburtsurkundeBefund);
        case "sterbeurkunde":
          return sterbeurkundeZuPrivatFormState(befund as SterbeurkundeBefund);
        case "heiratsurkunde":
          return heiratsurkundeZuPrivatFormState(befund as HeiratsurkundeBefund, {
            selbst: heirat?.selbst ?? 1,
            ehename: heirat?.ehename,
          });
        // Die vCard teilt den Befund der Visitenkarte. In der Personen-Maske
        // kennt sie zusätzlich private Kanäle und den Geburtstag; ohne Firma
        // ist sie ein privater Kontakt (T-356). Ob eine Firma genannt ist,
        // zählt am GANZEN Befund — `zielFelderVon` fragt je Einzelfeld.
        case "vcard":
          return ziel === "privat"
            ? vcardZuPrivatFormState(befund as VisitenkarteBefund, kontext, { privat: !vcardMitFirma })
            : visitenkarteZuFirmaFormState(befund as VisitenkarteBefund, kontext);
        default:
          return ziel === "privat"
            ? visitenkarteZuPrivatFormState(befund as VisitenkarteBefund, kontext)
            : visitenkarteZuFirmaFormState(befund as VisitenkarteBefund, kontext);
      }
    },
    [artId, ziel, kontext, heirat?.selbst, heirat?.ehename, vcardMitFirma],
  );

  /** Eine Aufnahme einlesen — je nach Art über den Server oder im Browser. */
  const lies = async (datei: File, welche: "vorne" | "hinten") => {
    if (!art) return;
    setLaeuft(welche);
    setFehler(null);
    setWarnung(null);
    // Ein neuer Scan ersetzt die Korrekturen — sie gehörten zum alten Befund.
    setKorrekturHinweis(
      Object.keys(korrekturen).length ? "Korrekturen wurden durch den neuen Scan ersetzt." : null,
    );
    setKorrekturen({});
    setEntwuerfe({});
    setEntwurfFehler({});
    const setzen = welche === "vorne" ? setVorderseite : setRueckseite;
    setzen({ datei, befund: null });

    try {
      if (art.id === "vcard") {
        const text = await leseVcardDatei(datei);
        if (!istVcard(text)) {
          throw new DokumentScanFehler(
            "Das ist keine vCard-Datei. Erwartet wird eine .vcf aus dem Adressbuch.",
          );
        }
        const { befund, anzahl } = vcardLesen(text);
        if (anzahl > 1) {
          setWarnung(
            `Die Datei enthält ${anzahl} Kontakte. Übernommen wird nur der erste — ` +
              "für einen Sammelimport ist diese Maske nicht gedacht.",
          );
        }
        setzen({ datei, befund });
        return;
      }

      const leser: Partial<Record<ScanArtId, (d: File) => Promise<{ felder: unknown }>>> = {
        personalausweis: lesePersonalausweis,
        gehaltsabrechnung: leseGehaltsabrechnung,
        fuehrerschein: leseFuehrerschein,
        geburtsurkunde: leseGeburtsurkunde,
        heiratsurkunde: leseHeiratsurkunde,
        sterbeurkunde: leseSterbeurkunde,
      };
      const antwort = await (leser[art.id] ?? leseVisitenkarte)(datei);
      setzen({ datei, befund: (antwort.felder ?? {}) as Befund });
    } catch (err) {
      setzen(null);
      setFehler(
        err instanceof DokumentScanFehler
          ? err.message
          : "Die Datei konnte nicht gelesen werden.",
      );
    } finally {
      setLaeuft(null);
    }
  };

  /** Welche Angaben hat die MRZ über ihre Prüfziffern bestätigt? */
  const geprueft = React.useMemo(
    () => new Set((felder.geprueft as string[] | undefined) ?? []),
    [felder],
  );

  /** Hinweise der Betragsprüfung (nur Gehaltsabrechnung). */
  const betragsHinweise = React.useMemo(
    () => (felder.hinweise as string[] | undefined) ?? [],
    [felder],
  );

  /** Anzeigewert eines Befundfelds — Beträge werden formatiert. */
  const anzeigewert = React.useCallback(
    (feld: string): string => {
      if (artId === "gehaltsabrechnung") {
        return gehaltAnzeigewert(felder as GehaltsabrechnungBefund, feld);
      }
      if (artId && ABLEGBAR.has(artId)) return urkundeAnzeigewert(felder, feld);
      const wert = felder[feld];
      return typeof wert === "string" ? wert : "";
    },
    [artId, felder],
  );

  /**
   * Der Firmenname, unter dem im Bestand gesucht wird: auf der Karte die
   * Firma, auf der Abrechnung der Arbeitgeber.
   */
  const firmenname = React.useMemo(() => {
    const wert =
      artId === "gehaltsabrechnung" ? felder.arbeitgeber : felder.firma;
    return typeof wert === "string" && wert.trim() ? wert.trim() : "";
  }, [artId, felder]);

  /** Domain der Karte (Website, sonst E-Mail) — Freemail zählt nicht. */
  const firmenDomainWert = React.useMemo(
    () =>
      artId === "visitenkarte" || artId === "vcard"
        ? firmenDomain(felder as VisitenkarteBefund)
        : undefined,
    [artId, felder],
  );

  /**
   * Steht die Firma schon im Bestand? (P3)
   *
   * Nur in der Personen-Maske nötig. Gesucht wird über den Namen ohne
   * Rechtsform — die Suche der Kontaktliste ist unscharf (Trigramme) — und
   * über die Domain der Karte. Welche Ergebnisse wirklich passen, entscheidet
   * `firmenTrefferBewerten`; ein unscharfer Treffer wird nie still verknüpft,
   * der Dialog zeigt die Treffer zur Auswahl.
   *
   * In der Firmen-Maske entfällt die Suche: Die Person wird dort als noch
   * nicht gespeicherter Eintrag gesammelt und erst beim Speichern der Firma
   * angelegt — so, wie es die Verknüpfungs-Erfassung ohnehin handhabt.
   */
  React.useEffect(() => {
    if (ziel !== "privat" || !firmenname) {
      setTrefferListe([]);
      setGewaehlteFirmaId(null);
      return;
    }
    const ctrl = new AbortController();
    const suche = (begriff: string | undefined) =>
      begriff
        ? fetch(apiUrl("contact", { search: begriff, limit: 50 }), { signal: ctrl.signal })
            .then((r) => (r.ok ? r.json() : []))
            .then(alsBestandsFirmen)
            .catch(() => [])
        : Promise.resolve([]);
    setSucheLaeuft(true);
    Promise.all([suche(firmenSuchbegriff(firmenname) || firmenname), suche(firmenDomainWert)])
      .then(([namensErgebnisse, domainErgebnisse]) => {
        if (ctrl.signal.aborted) return;
        const liste = firmenTrefferBewerten({
          firmenname,
          domain: firmenDomainWert,
          namensErgebnisse,
          domainErgebnisse,
        });
        setTrefferListe(liste);
        // Vorgewählt ist der beste Treffer; die Auswahl bleibt sichtbar.
        setGewaehlteFirmaId(liste[0]?.id ?? null);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setSucheLaeuft(false);
      });
    return () => ctrl.abort();
  }, [ziel, firmenname, firmenDomainWert]);

  const treffer = React.useMemo(
    () => trefferListe.find((t) => t.id === gewaehlteFirmaId) ?? null,
    [trefferListe, gewaehlteFirmaId],
  );

  /**
   * Welche Formularfelder ein einzelner Befundwert füllen würde. Ermittelt über
   * denselben Mapper — so gibt es keine zweite Zuordnungstabelle, die von der
   * ersten abdriften könnte.
   */
  const zielFelderVon = React.useCallback(
    (feld: string): string[] => Object.keys(mappe({ [feld]: felder[feld] }).werte),
    [mappe, felder],
  );

  // Ein geleertes Feld bleibt als Zeile stehen — sonst ließe es sich nicht
  // zurückholen (T-84).
  const gefundeneFelder = (art?.reihenfolge ?? []).filter(
    (feld) => anzeigewert(feld).trim() !== "" || feld in korrekturen,
  );
  const istGeleert = (feld: string): boolean => feld in korrekturen && korrekturen[feld] === undefined;
  const istKorrigiert = (feld: string): boolean => feld in korrekturen;

  /**
   * Darf der Wert korrigiert werden? Nicht, wenn das Zielfeld in der Maske
   * schon belegt ist (das bleibt unangetastet), und nicht, wenn das Recht
   * fehlt — eine Korrektur schaltet kein Feld frei. Katalog-Gründe (Anrede,
   * Titel, Rechtsform) bleiben korrigierbar: Erst die Korrektur macht den
   * Wert übernehmbar.
   */
  const istKorrigierbar = (feld: string): boolean =>
    !istBelegt(feld) && !(istOhneZiel(feld) && /darfst/.test(grundFuer(feld) ?? ""));

  /** Der Text, der in der Zeile steht: der Entwurf, sonst der (korrigierte) Wert. */
  const eingabeText = (feld: string): string =>
    feld in entwuerfe ? entwuerfe[feld] : istGeleert(feld) ? "" : anzeigewert(feld);

  const ohneSchluessel = <T,>(obj: Record<string, T>, feld: string): Record<string, T> => {
    if (!(feld in obj)) return obj;
    const neu = { ...obj };
    delete neu[feld];
    return neu;
  };

  /** Tippen in der Zeile: Entwurf merken, die Zeile ist damit gewollt. */
  const eingabeAendern = (feld: string, text: string) => {
    setEntwuerfe((vorher) => ({ ...vorher, [feld]: text }));
    setEntwurfFehler((vorher) => ohneSchluessel(vorher, feld));
    setAbgewaehlt((vorher) => {
      if (!vorher.has(feld)) return vorher;
      const neu = new Set(vorher);
      neu.delete(feld);
      return neu;
    });
  };

  /** Feld verlassen / Enter: den Entwurf prüfen und als Korrektur übernehmen. */
  const eingabeUebernehmen = (feld: string) => {
    if (!(feld in entwuerfe)) return;
    const text = entwuerfe[feld];
    const meldung = korrekturPruefen(feld, text, artId);
    if (meldung) {
      setEntwurfFehler((vorher) => ({ ...vorher, [feld]: meldung }));
      return;
    }
    const wert = korrekturWert(feld, text, artId);
    setKorrekturen((vorher) => {
      const neu = { ...vorher };
      // Unverändert gegenüber dem Erkannten → keine Korrektur.
      if (JSON.stringify(wert) === JSON.stringify(befundFelder[feld])) delete neu[feld];
      else neu[feld] = wert;
      return neu;
    });
    setEntwuerfe((vorher) => ohneSchluessel(vorher, feld));
    setEntwurfFehler((vorher) => ohneSchluessel(vorher, feld));
  };

  /** Escape: den Entwurf verwerfen, der bisherige Wert steht wieder da. */
  const eingabeVerwerfen = (feld: string) => {
    setEntwuerfe((vorher) => ohneSchluessel(vorher, feld));
    setEntwurfFehler((vorher) => ohneSchluessel(vorher, feld));
  };

  const korrekturZuruecknehmen = (feld: string) => {
    setKorrekturen((vorher) => ohneSchluessel(vorher, feld));
    eingabeVerwerfen(feld);
  };

  const istBelegt = (feld: string): boolean =>
    zielFelderVon(feld).some((key) => (vorhanden[key] ?? "").trim() !== "");

  const istOhneZiel = (feld: string): boolean => zielFelderVon(feld).length === 0;

  /**
   * Warum ein Wert nicht übernommen wird — vom Mapper erfragt statt hier
   * geraten. „Kein Feld" und „darfst du nicht speichern" sehen in der Liste
   * sonst gleich aus, sind aber grundverschieden: das eine kann niemand
   * ändern, das andere die Verantwortlichkeit.
   */
  const grundFuer = React.useCallback(
    (feld: string): string | undefined =>
      mappe({ [feld]: felder[feld] }).uebergangen[0]?.grund,
    [mappe, felder],
  );

  const istGewaehlt = (feld: string): boolean => {
    if (abgewaehlt.has(feld)) return false;
    if (istOhneZiel(feld)) return false;
    return !istBelegt(feld);
  };

  const umschalten = (feld: string) =>
    setAbgewaehlt((vorher) => {
      const neu = new Set(vorher);
      if (neu.has(feld)) neu.delete(feld);
      else neu.add(feld);
      return neu;
    });

  /**
   * Ist eine Gegenseite genannt, die verknüpft werden kann?
   *
   * Der Ausweis nennt keine — er ist ein reines Personendokument.
   */
  const gegenseite: { art: "firma" | "person"; name: string } | null = React.useMemo(() => {
    if (!gegenseiteMoeglich || !artId || !MIT_GEGENSEITE.has(artId)) return null;
    if (ziel === "privat") return firmenname ? { art: "firma", name: firmenname } : null;
    const vorname = typeof felder.vorname === "string" ? felder.vorname : "";
    const nachname = typeof felder.nachname === "string" ? felder.nachname : "";
    const name = [vorname, nachname].filter(Boolean).join(" ");
    return name ? { art: "person", name } : null;
  }, [gegenseiteMoeglich, artId, ziel, firmenname, felder]);

  /**
   * Der Befund, aus dem eine Firma angelegt wird. Die Abrechnung nennt den
   * Arbeitgeber unter eigenen Schlüsseln — umgesetzt auf die Kartenform, damit
   * beide Wege dieselbe Anlage-Funktion benutzen.
   */
  const firmenBefund = React.useCallback((): VisitenkarteBefund => {
    if (artId !== "gehaltsabrechnung") return felder as VisitenkarteBefund;
    const s = (wert: unknown): string | undefined =>
      typeof wert === "string" && wert.trim() ? wert.trim() : undefined;
    return {
      firma: s(felder.arbeitgeber),
      strasse: s(felder.arbeitgeberStrasse),
      plz: s(felder.arbeitgeberPlz),
      ort: s(felder.arbeitgeberOrt),
    };
  }, [artId, felder]);

  const uebernehmen = () => {
    const auswahl: Befund = {};
    for (const feld of gefundeneFelder) {
      if (istGewaehlt(feld)) auswahl[feld] = felder[feld];
    }

    const ergebnis: DateiScanUebernahme = { werte: mappe(auswahl).werte };
    if (artId) ergebnis.scanArt = artId;
    if (heirat) {
      // Die Urkunde belegt den Familienstand. Ein schon gewählter bleibt —
      // der Dialog zeigt, dass er unverändert bleibt (U9).
      if (!(vorhanden.familyStatus ?? "").trim()) ergebnis.werte.familyStatus = FAMILIENSTAND_VERHEIRATET;
      if (ehepartnerAn) {
        const partner = heiratEhepartner(heirat.befund, {
          selbst: heirat.selbst,
          ehename: heirat.ehename,
          anrede: vorhanden.anrede,
        });
        if (partner) ergebnis.ehepartner = partner;
      }
    }
    if (artId && ABLEGBAR.has(artId) && dateiAblegen && vorderseite?.datei) {
      ergebnis.ablegen = vorderseite.datei;
    }

    if (gegenseite && gegenseiteAn) {
      if (gegenseite.art === "person") {
        // Wird erst beim Speichern der Firma angelegt (`manual: true`).
        const eintrag = visitenkarteZuMenschEntry(felder as VisitenkarteBefund, kontext);
        if (eintrag) ergebnis.mensch = eintrag;
      } else {
        // Die Firma kommt in „Verbindung zu Unternehmen" — bestehend mit ihrer
        // Id, sonst als NEUE, vorbefüllte Referenz. Angelegt wird sie erst beim
        // Speichern der Person (P9): Bis 09/2026 legte der Dialog sie hier
        // sofort an, und nach einem Abbruch blieb eine verwaiste Firma zurück.
        const ref: ContactRef | null = treffer
          ? { contactId: treffer.id, type: "Firma", lastName: treffer.name, displayName: treffer.name }
          : visitenkarteZuFirmaRef(firmenBefund(), kontext);
        const position =
          artId === "gehaltsabrechnung"
            ? undefined
            : typeof felder.position === "string"
              ? felder.position
              : undefined;
        if (ref) ergebnis.firmaVerbindung = { ref, ...(position ? { position } : {}) };
      }
    }

    // Die Kanäle der Person tragen den Bezug zur Firma der Karte (P8) — auch
    // wenn der Anwender die Verknüpfung abwählt, bleibt der Name, unter dem
    // sie zugeordnet werden.
    if (ziel === "privat" && (artId === "visitenkarte" || artId === "vcard") && firmenname) {
      ergebnis.firma = gegenseiteAn && treffer ? { id: treffer.id, name: treffer.name } : { name: firmenname };
    }

    onUebernehmen(ergebnis);
    zuruecksetzen();
    onClose();
  };

  // Hinweise auf Werte, die in dieser Maske kein Ziel haben — sie gehen sonst
  // still verloren, obwohl sie auf dem Dokument stehen. Was die Verknüpfung zur
  // Gegenseite mitnimmt, ist NICHT übergangen und wird hier ausgeblendet: es
  // stünde sonst gleichzeitig als „wird ergänzt" und „nicht übernommen" da.
  // Die Anschrift der Karte ist Firmenanschrift: Sie geht mit, wenn die Firma
  // hier NEU angelegt wird. Steht die Firma schon im Bestand, hat sie ihre
  // Anschrift — dann bleibt der Hinweis sichtbar.
  const durchGegenseiteAbgedeckt = React.useMemo(
    () =>
      ziel === "privat"
        ? new Set([
            "Firma",
            "Arbeitgeber",
            ...(treffer
              ? []
              : [
                  "Straße",
                  "PLZ",
                  "Ort",
                  "Land",
                  "Telefon (Zentrale)",
                  "E-Mail (Zentrale)",
                  "Fax",
                  "Website",
                  "USt-IdNr.",
                  "Handelsregister",
                  "Registergericht",
                ]),
          ])
        : // Aus dem Label-Katalog statt als Text — sonst laufen Beschriftung und
          // Filter auseinander, und die Adresse stünde doppelt da (T-84).
          new Set(
            (["anredeText", "titel", "vorname", "nachname", "position", "telefon", "mobil", "email"] as const).map(
              (k) => BEFUND_LABELS[k],
            ),
          ),
    [ziel, treffer],
  );
  const uebergangen = React.useMemo(() => {
    const alle = mappe(felder).uebergangen;
    // Heiratsurkunde: Was der Ehepartner-Vorschlag mitnimmt, ist nicht übergangen.
    if (heirat && ehepartnerAn) {
      const andere = heirat.selbst === 1 ? 2 : 1;
      return alle.filter((u) => !u.feld.startsWith(`Ehegatte ${andere}:`));
    }
    return gegenseiteAn && gegenseite
      ? alle.filter((u) => !durchGegenseiteAbgedeckt.has(u.feld))
      : alle;
  }, [mappe, felder, gegenseiteAn, gegenseite, durchGegenseiteAbgedeckt, heirat, ehepartnerAn]);

  const anzahlGewaehlt = gefundeneFelder.filter((feld) => istGewaehlt(feld)).length;

  if (!open) return null;

  const kachel = (
    welche: "vorne" | "hinten",
    seite: Seite | null,
    ref: React.RefObject<HTMLInputElement>,
    titel: string,
    hinweis: string,
  ) => (
    <button
      type="button"
      className={mergeClasses(styles.kachel, seite ? styles.kachelGefuellt : undefined)}
      onClick={() => ref.current?.click()}
      disabled={laeuft !== null}
      aria-busy={laeuft === welche}
    >
      <span className={styles.kachelTitel}>{titel}</span>
      <span className={styles.kachelHinweis}>
        {laeuft === welche ? "wird gelesen …" : seite ? seite.datei.name : hinweis}
      </span>
      {seite && hatVorschau(seite.datei) && (
        <img
          className={styles.vorschau}
          src={URL.createObjectURL(seite.datei)}
          alt=""
          onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
        />
      )}
    </button>
  );

  /**
   * Der zweite Weg zu einer Seite: ein Dokument, das schon in KMVS liegt.
   *
   * Gelesen wird es wie jede andere Datei — heruntergeladen, durch `lies()`
   * geschickt, ausgewertet. GESPEICHERT WIRD WEITERHIN NICHTS: das Dokument
   * liegt schon in KMVS und bleibt, wo es ist; übernommen werden nur die
   * Felder, die der Nutzer unten auswählt (§ 20 PAuswG, siehe Dateikopf).
   */
  const holeAusKmvs = async (auswahl: DokumentAuswahlErgebnis) => {
    const welche = kmvsFuer;
    setKmvsFuer(null);
    const doc = auswahl.dokumente[0];
    const datei = auswahl.dateien[0];
    if (!welche) return;
    if (datei) {
      void lies(datei, welche);
      return;
    }
    if (!doc) return;
    try {
      await lies(await dokumentAlsDatei(doc), welche);
    } catch (err) {
      console.error("[DateiScannen] Dokument konnte nicht geladen werden", err);
      setFehler("Das Dokument konnte nicht geladen werden.");
    }
  };

  const dateiFeld = (welche: "vorne" | "hinten", ref: React.RefObject<HTMLInputElement>) => (
    <input
      ref={ref}
      type="file"
      accept={art?.accept ?? BELEG_FORMATE}
      className={styles.versteckt}
      onChange={(e) => {
        const datei = e.target.files?.[0];
        e.target.value = "";
        if (datei) void lies(datei, welche);
      }}
    />
  );

  return createPortal(
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Datei scannen"
      onClick={(e) => {
        if (e.target === e.currentTarget) schliessen();
      }}
    >
      <div className={styles.surface}>
        <div className={styles.kopf}>
          <h2 className={styles.titel}>
            {art ? art.titel : titel}
          </h2>
        </div>

        <div className={styles.koerper}>
          {!art ? (
            <>
              <div style={{ marginBottom: 12 }}>Was möchtest du einlesen?</div>
              <div className={styles.artGitter}>
                {verfuegbar.map((a) => {
                  const Icon = a.icon;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={mergeClasses(styles.artKachel, a.id === "vcard" && styles.artKachelHervor)}
                      onClick={() => {
                        befundZuruecksetzen();
                        setArtId(a.id);
                      }}
                    >
                      <span className={styles.artIcon}>
                        <Icon />
                      </span>
                      <span>
                        <span className={styles.artTitel}>{a.titel}</span>
                        <span className={styles.artHinweis}>{a.hinweis}</span>
                      </span>
                    </button>
                  );
                })}
                {folgendeArten.map((t) => (
                  <button
                    key={t}
                    type="button"
                    disabled
                    aria-disabled="true"
                    className={mergeClasses(styles.artKachel, styles.artKachelAus)}
                    title="Die Texterkennung für diese Dokumentart folgt."
                  >
                    <span className={styles.artIcon}>
                      <IconAusweis />
                    </span>
                    <span>
                      <span className={styles.artTitel}>{t}</span>
                      <span className={styles.artHinweis}>Texterkennung folgt</span>
                    </span>
                  </button>
                ))}
              </div>
              {ziel === "firma" && !erlaubteArten && (
                <div className={styles.hinweisKasten}>
                  Personalausweis und Gehaltsabrechnung gehören zu einer Person und
                  stehen deshalb nur in der Personen-Maske zur Verfügung.
                </div>
              )}
            </>
          ) : (
            <>
              {artWechselbar && (
                <button
                  type="button"
                  className={styles.zurueck}
                  onClick={() => {
                    befundZuruecksetzen();
                    setArtId(null);
                  }}
                >
                  ← andere Art wählen
                </button>
              )}

              <div className={styles.seiten}>
                {art.seiten.map((s) =>
                  s.welche === "vorne"
                    ? kachel("vorne", vorderseite, vorneRef, s.titel, s.hinweis)
                    : kachel("hinten", rueckseite, hintenRef, s.titel, s.hinweis),
                )}
              </div>

              {/* Der zweite Weg (PO 22.09.2026): Der Beleg kann schon in
                  KMVS liegen — etwa der Ausweis-Scan, den ein Kollege zur
                  Legitimation abgelegt hat. Ihn erst herunterzuladen, um ihn
                  gleich wieder auszuwählen, war der einzige Weg dorthin. */}
              <div className={styles.kmvsZeile}>
                {art.seiten.map((s2) => (
                  <button
                    key={s2.welche}
                    type="button"
                    className={styles.kmvsKnopf}
                    disabled={laeuft !== null}
                    onClick={() => setKmvsFuer(s2.welche)}
                  >
                    {art.seiten.length > 1
                      ? `${s2.titel}: aus KMVS wählen`
                      : "Aus KMVS wählen"}
                  </button>
                ))}
              </div>

              {dateiFeld("vorne", vorneRef)}
              {art.seiten.length > 1 && dateiFeld("hinten", hintenRef)}

              {art.id === "vcard" && (
                <div className={styles.hinweisKasten}>
                  <strong>iPhone:</strong> In „Kontakte“ den Kontakt öffnen → „Kontakt teilen“ →
                  „In Dateien sichern“. Dann hier die Datei auswählen.
                  <br />
                  <strong>Android:</strong> Im Adressbuch den Kontakt teilen bzw. exportieren
                  (.vcf) und hier auswählen.
                </div>
              )}

              {art.id === "personalausweis" && (
                <div className={styles.hinweisKasten}>
                  Der Scan wird <strong>nicht gespeichert</strong> — übernommen werden nur
                  die Felder, die du unten auswählst. Ausweisdaten dürfen nur mit
                  Einverständnis der betroffenen Person erfasst werden.
                </div>
              )}

              {fehler && <div className={styles.fehler}>{fehler}</div>}
              {warnung && <div className={styles.hinweisKasten}>{warnung}</div>}
              {korrekturHinweis && <div className={styles.hinweisKasten}>{korrekturHinweis}</div>}

              {vorderseite?.befund && gefundeneFelder.length === 0 && laeuft === null && (
                <div className={styles.hinweisKasten}>
                  {art.id === "vcard"
                    ? "In der Datei standen keine verwertbaren Angaben."
                    : "Auf der Aufnahme war nichts Verwertbares zu lesen. Bitte das Dokument formatfüllend und gut ausgeleuchtet aufnehmen — oder die Felder von Hand ausfüllen."}
                </div>
              )}

              {gefundeneFelder.length > 0 && (
                <>
                  <div style={{ marginBottom: 6, fontWeight: 700, color: REFINED.ink }}>
                    Gefunden — bitte prüfen:
                  </div>
                  {gefundeneFelder.map((feld) => {
                    const label = art.labels[feld] ?? feld;
                    const geleert = istGeleert(feld);
                    const wert = geleert ? "" : anzeigewert(feld);
                    const belegt = istBelegt(feld);
                    const ohneZiel = istOhneZiel(feld);
                    const korrigiert = istKorrigiert(feld);
                    // T-84: Die Kanäle der Person landen in der Personen-Maske
                    // in der Spalte „Geschäftlich" mit Bezug zur Firma der Karte.
                    const geschaeftlich =
                      ziel === "privat" &&
                      (art.id === "visitenkarte" || art.id === "vcard") &&
                      (feld === "telefon" || feld === "mobil" || feld === "email") &&
                      !belegt &&
                      !ohneZiel;

                    // T-84: Der Wert steht direkt als Eingabefeld in der Zeile —
                    // wie bei „Daten aus dem Impressum befüllen". Gesperrt
                    // (Zielfeld belegt, kein Recht) bleibt er reiner Text.
                    const editierbar = istKorrigierbar(feld);
                    const eingabeId = `scan-wert-${feld}`;
                    const fehlerText = entwurfFehler[feld];
                    const typ = korrekturTyp(feld, art.id);
                    return (
                      <div key={feld} className={styles.zeile} data-scan-zeile={feld}>
                        <input
                          type="checkbox"
                          className={styles.zeileHaken}
                          checked={istGewaehlt(feld)}
                          disabled={belegt || ohneZiel}
                          onChange={() => umschalten(feld)}
                          aria-label={`${label} übernehmen`}
                        />
                        {editierbar ? (
                          <label className={styles.zeileLabel} htmlFor={eingabeId}>
                            {label}
                          </label>
                        ) : (
                          <span className={styles.zeileLabel}>{label}</span>
                        )}
                        <span className={styles.zeileWert}>
                          {editierbar ? (
                            <input
                              id={eingabeId}
                              className={mergeClasses(inline.eingabe, fehlerText ? inline.ungueltig : undefined)}
                              type="text"
                              inputMode={korrekturInputMode(typ)}
                              value={eingabeText(feld)}
                              disabled={laeuft !== null}
                              aria-invalid={fehlerText ? true : undefined}
                              placeholder={geleert ? "geleert" : undefined}
                              onChange={(e) => eingabeAendern(feld, e.target.value)}
                              onBlur={() => eingabeUebernehmen(feld)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  eingabeUebernehmen(feld);
                                } else if (e.key === "Escape" && feld in entwuerfe) {
                                  // Nur den Entwurf verwerfen, nicht den Dialog schließen.
                                  e.preventDefault();
                                  e.stopPropagation();
                                  eingabeVerwerfen(feld);
                                }
                              }}
                            />
                          ) : (
                            <span className={styles.zeileText}>{wert}</span>
                          )}
                          {fehlerText && (
                            <div className={styles.korrekturFehler} role="alert">
                              {fehlerText}
                            </div>
                          )}
                          {geprueft.has(feld) && (
                            <span className={styles.geprueft}>✓ Prüfziffer stimmt</span>
                          )}
                          {feld === "telefonZentrale" && felder.zentraleAbgeleitet === "ja" && !korrigiert && (
                            <div className={styles.belegt}>
                              Steht nicht auf der Karte — aus der Durchwahl abgeleitet (Vorschlag)
                            </div>
                          )}
                          {geschaeftlich && (
                            <div className={styles.belegt}>
                              wird als geschäftlicher Kanal eingetragen
                              {firmenname ? ` (${firmenname})` : ""}
                            </div>
                          )}
                          {belegt && (
                            <div className={styles.belegt}>
                              Feld ist bereits ausgefüllt — bleibt unverändert
                            </div>
                          )}
                          {geleert && (
                            <div className={styles.belegt}>geleert — wird nicht übernommen</div>
                          )}
                          {ohneZiel && !belegt && !geleert && (
                            <div className={styles.belegt}>
                              {grundFuer(feld) ?? "in dieser Maske kein passendes Feld"}
                            </div>
                          )}
                          {korrigiert && (
                            <div className={styles.belegt}>
                              geändert ·{" "}
                              <button
                                type="button"
                                className={styles.kmvsKnopf}
                                onClick={(e) => {
                                  e.preventDefault();
                                  korrekturZuruecknehmen(feld);
                                }}
                              >
                                Zurück zum erkannten Wert
                              </button>
                            </div>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}

              {art.id === "personalausweis" &&
                gefundeneFelder.length > 0 &&
                !felder.mrz && (
                  <div className={styles.hinweisKasten}>
                    Die maschinenlesbare Zone wurde nicht erfasst. Ohne sie lassen sich
                    Ausweisnummer und Datum nicht gegenprüfen — bitte die Rückseite so
                    aufnehmen, dass die drei Zeilen am unteren Rand scharf sind.
                  </div>
                )}

              {Array.isArray(felder.verworfen) && (felder.verworfen as unknown[]).length > 0 && (
                <div className={styles.hinweisKasten}>
                  <strong>Nicht angeboten</strong> — die Texterkennung meldete diese Werte,
                  sie stehen aber so nicht im erkannten Text. Bitte am Dokument selbst prüfen:
                  <ul className={styles.liste}>
                    {(felder.verworfen as Array<{ feld: string; wert: string }>).map((v) => (
                      <li key={`${v.feld}-${v.wert}`}>
                        {art.labels[v.feld] ?? v.feld}: „{v.wert}"
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {ABLEGBAR.has(art.id) && vorderseite?.datei && (
                <div className={styles.hinweisKasten}>
                  <label style={{ display: "flex", alignItems: "flex-start", columnGap: 10 }}>
                    <input
                      type="checkbox"
                      checked={dateiAblegen}
                      onChange={() => setDateiAblegen((v) => !v)}
                    />
                    <span>
                      Datei „{vorderseite.datei.name}" als {art.titel} in der Akte ablegen
                      <div className={styles.belegt}>
                        Abgelegt wird beim Speichern des Kontakts.
                      </div>
                    </span>
                  </label>
                </div>
              )}

              {heirat && gefundeneFelder.length > 0 && (() => {
                const name = (nr: 1 | 2) =>
                  [heirat.befund[`ehegatte${nr}Vornamen`], heirat.befund[`ehegatte${nr}Nachname`]]
                    .filter(Boolean)
                    .join(" ") || `Ehegatte ${nr}`;
                const andere = heirat.selbst === 1 ? 2 : 1;
                const partner = heiratEhepartner(heirat.befund, {
                  selbst: heirat.selbst,
                  ehename: heirat.ehename,
                  anrede: vorhanden.anrede,
                });
                const familienstand = (vorhanden.familyStatus ?? "").trim();
                return (
                  <div className={styles.hinweisKasten}>
                    <div role="radiogroup" aria-label="Welcher Ehegatte ist dieser Kontakt?">
                      <strong>Dieser Kontakt ist</strong>
                      {!heirat.sicher && (
                        <span className={styles.belegt} style={{ display: "inline" }}>
                          {" "}— nicht eindeutig erkannt, bitte prüfen
                        </span>
                      )}
                      {([1, 2] as const).map((nr) => (
                        <label key={nr} style={{ display: "flex", alignItems: "center", columnGap: 8 }}>
                          <input
                            type="radio"
                            name="scan-ehegatte"
                            checked={heirat.selbst === nr}
                            onChange={() => setEhegatteGewaehlt(nr)}
                          />
                          <span>
                            Ehegatte {nr}: <strong>{name(nr)}</strong>
                          </span>
                        </label>
                      ))}
                    </div>
                    {partner && (
                      <label style={{ display: "flex", alignItems: "flex-start", columnGap: 10, marginTop: 8 }}>
                        <input type="checkbox" checked={ehepartnerAn} onChange={() => setEhepartnerAn((v) => !v)} />
                        <span>
                          <strong>{name(andere)}</strong> als{" "}
                          {partner.relationLabel || "Ehepartner"} im Haushalt vorschlagen
                          <div className={styles.belegt}>
                            Wird unter „Haushalt" eingetragen und erst beim Speichern angelegt.
                            {!partner.relationLabel && " Die Beziehung (Ehemann/Ehefrau) bitte dort wählen."}
                          </div>
                        </span>
                      </label>
                    )}
                    <div className={styles.belegt} style={{ marginTop: 6 }}>
                      {familienstand
                        ? `Familienstand steht auf „${familienstand}" — bleibt unverändert.`
                        : `Familienstand wird auf „${FAMILIENSTAND_VERHEIRATET}" gesetzt.`}
                    </div>
                  </div>
                );
              })()}

              {betragsHinweise.length > 0 && (
                <div className={styles.hinweisKasten}>
                  <strong>Beträge, die nicht zusammenpassen</strong> — diese Werte wurden
                  nicht übernommen:
                  <ul className={styles.liste}>
                    {betragsHinweise.map((h) => (
                      <li key={h}>{h}</li>
                    ))}
                  </ul>
                </div>
              )}

              {gegenseite && (
                <div className={styles.hinweisKasten}>
                  <label style={{ display: "flex", alignItems: "flex-start", columnGap: 10 }}>
                    <input
                      type="checkbox"
                      checked={gegenseiteAn}
                      onChange={() => setGegenseiteAn((v) => !v)}
                    />
                    <span>
                      {gegenseite.art === "person" ? (
                        <>
                          <strong>{gegenseite.name}</strong> als Mensch im Unternehmen ergänzen
                          {typeof felder.position === "string" && felder.position
                            ? ` (${felder.position})`
                            : ""}
                          .
                          <div className={styles.belegt}>
                            Die Person wird zusammen mit der Firma angelegt. Organrollen wie
                            „Geschäftsführer" trägt der Scan bewusst nicht ein — daran hängen
                            Organigramm und Vertretung, das gehört bestätigt.
                          </div>
                        </>
                      ) : sucheLaeuft ? (
                        <>
                          Firma <strong>{gegenseite.name}</strong> wird im Bestand gesucht …
                        </>
                      ) : (
                        <>
                          <strong>{gegenseite.name}</strong> unter „Verbindung zu Unternehmen"
                          eintragen
                          {typeof felder.position === "string" && felder.position && art.id !== "gehaltsabrechnung"
                            ? ` (${felder.position})`
                            : ""}
                          .
                          <div className={styles.belegt}>
                            Die Verbindung wird erst beim Speichern des Kontakts angelegt.
                          </div>
                        </>
                      )}
                    </span>
                  </label>
                  {gegenseite.art === "firma" && gegenseiteAn && !sucheLaeuft && (
                    <div role="radiogroup" aria-label="Firma im Bestand" style={{ marginTop: 8, marginLeft: 26 }}>
                      {trefferListe.length > 0 && (
                        <div className={styles.belegt} style={{ marginBottom: 4 }}>
                          {trefferListe.length === 1
                            ? "Im Bestand gefunden — bitte bestätigen:"
                            : "Mehrere Firmen im Bestand passen — bitte wählen:"}
                        </div>
                      )}
                      {trefferListe.map((t) => (
                        <label key={t.id} style={{ display: "flex", alignItems: "center", columnGap: 8 }}>
                          <input
                            type="radio"
                            name="scan-firmenwahl"
                            checked={gewaehlteFirmaId === t.id}
                            onChange={() => setGewaehlteFirmaId(t.id)}
                          />
                          <span>
                            <strong>{t.name}</strong>{" "}
                            <span className={styles.belegt} style={{ display: "inline" }}>
                              {t.grund === "name"
                                ? "gleicher Name"
                                : t.grund === "name-aehnlich"
                                  ? "ähnlicher Name"
                                  : `gleiche Domain (${firmenDomainWert ?? ""})`}
                            </span>
                          </span>
                        </label>
                      ))}
                      <label style={{ display: "flex", alignItems: "center", columnGap: 8 }}>
                        <input
                          type="radio"
                          name="scan-firmenwahl"
                          checked={gewaehlteFirmaId === null}
                          onChange={() => setGewaehlteFirmaId(null)}
                        />
                        <span>
                          {trefferListe.length ? "Keine davon — " : ""}
                          <strong>als neue Firma anlegen</strong>
                          <span className={styles.belegt} style={{ display: "block" }}>
                            {art.id === "gehaltsabrechnung"
                              ? "Die Anschrift des Arbeitgebers von der Abrechnung kommt an die Firma."
                              : "Name, Rechtsform, Anschrift, Website, Zentrale, Fax, USt-IdNr. und Register der Karte kommen an die Firma (Schnellanlage „Neu“)."}
                          </span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {konflikte.length > 0 && (
                <div className={styles.hinweisKasten}>
                  <strong>Vorder- und Rückseite widersprechen sich.</strong> Übernommen wird
                  der Wert der Vorderseite:
                  <ul className={styles.liste}>
                    {konflikte.map((k) => (
                      <li key={k.feld}>
                        {art.labels[k.feld] ?? k.feld}: „{k.vorne}" statt „{k.hinten}"
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uebergangen.length > 0 && (
                <div className={styles.hinweisKasten}>
                  <strong>Nicht übernommen</strong> — steht auf dem Dokument, hat hier aber
                  kein Feld:
                  <ul className={styles.liste}>
                    {uebergangen.map((u) => (
                      <li key={`${u.feld}-${u.wert}`}>
                        {u.feld}: „{u.wert}" ({u.grund})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.fuss}>
          <button type="button" className={styles.btn} onClick={schliessen}>
            Abbrechen
          </button>
          {art && (
            <button
              type="button"
              className={mergeClasses(styles.btn, styles.btnPrimaer)}
              onClick={uebernehmen}
              disabled={
                (anzahlGewaehlt === 0 &&
                  !(gegenseite && gegenseiteAn) &&
                  !(ABLEGBAR.has(art.id) && dateiAblegen && vorderseite?.datei)) ||
                laeuft !== null ||
                sucheLaeuft ||
                Object.keys(entwurfFehler).length > 0
              }
            >
              {anzahlGewaehlt > 0 ? `${anzahlGewaehlt} Felder übernehmen` : "Übernehmen"}
            </button>
          )}
        </div>
      </div>

      {/* Die Auswahl als ZWEITE Ebene über dem Scan-Dialog: Sie wird aus ihm
          heraus geöffnet und muss ihn verdecken. In der Akte (Bearbeiten,
          Umwandeln) ist der Kontakt bekannt und vorbelegt (T-41); nur in der
          Neuanlage entsteht er hier gerade erst — dann zeigt die Fläche ihren
          eigenen Kundenpicker. */}
      {kmvsFuer && (
        <div
          className={styles.auswahlBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label="Dokument aus KMVS wählen"
          onClick={(e) => {
            if (e.target === e.currentTarget) setKmvsFuer(null);
          }}
        >
          <div className={mergeClasses(styles.auswahlFlaeche, "dok-aw-huelle")}>
            <DokumentAuswahl
              contactId={kontakt?.id ?? null}
              kundeName={kontakt?.name ?? null}
              einzeln
              {...(art?.accept ? { accept: art.accept } : {})}
              uebernehmenText="Lesen"
              onUebernehmen={(auswahl) => void holeAusKmvs(auswahl)}
              onAbbrechen={() => setKmvsFuer(null)}
            />
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default DateiScannenDialog;
